import { tv } from "tailwind-variants";

import type {
  ConfigTextSuggestion,
  DurationUnit,
  PractitionerSource,
} from "@/types/clinic-configuration";

/**
 * Styling for the always-mounted editable config controls, keyed by the
 * field's `appearance` — how much input chrome it shows at rest.
 *
 * `subtle`, the default, keeps a value reading as text until it is hovered
 * or focused, so a config page reads as a document rather than a form.
 *
 * `standard` is mostly the absence of the `subtle` overrides: `Input` and
 * `Select` already draw their own border, background and padding. Only the
 * controls whose own look differs from theirs (`EditableSelect`'s input and
 * the config textarea) restate it here.
 */
export const configFieldVariants = tv({
  slots: {
    /**
     * `Input`'s container and `Select`'s trigger — the element that owns the
     * border, background and padding.
     */
    field: "",
    /**
     * The native `<input>` inside `Input`, via its `inputClassName`.
     * `hover:`/`focus:` have to sit here rather than on `field`, since the
     * `<input>` is what actually receives them.
     */
    input: "",
    /**
     * `ConfigTextareaControl`, which styles its `<textarea>` directly rather
     * than a wrapper.
     */
    textarea: "",
    /**
     * The `<input>` of `EditableSelect`, which draws its own border, so it
     * takes this on top of `field`. Its own look differs from `Input`'s and
     * the `Select` trigger's (`code` fields): taller, another border and
     * focus ring. `standard` sets `field` to nothing, so this slot carries
     * the look those two share. It keeps full
     * opacity when disabled, as `Input` does, so a disabled `fieldset`
     * (a drawer still loading) does not fade this field alone.
     */
    editableSelect: "disabled:opacity-100",
  },
  variants: {
    appearance: {
      subtle: {
        /**
         * Transparent border rather than `border-0`: reserving the width up
         * front means only its *color* changes on focus, so the box never
         * resizes and nothing around it reflows.
         *
         * Padding is `0` at rest so the text lines up with the label above,
         * and grows once the border is visible and the text needs room
         * inside it. Growing the padding rather than moving the box keeps
         * the footprint fixed — only the text slides, and `padding` is in
         * the transition list so that slide eases instead of snapping.
         */
        field: [
          "h-8 rounded-lg border border-transparent bg-transparent px-0 shadow-none",
          "hover:bg-surface-hovered hover:px-2",
          "has-focus-visible:border-border-strong has-focus-visible:bg-card has-focus-visible:px-2 has-focus-visible:shadow-[inset_0_1px_0_rgba(0,0,0,0.04)]",
          "focus-visible:border-border-strong focus-visible:bg-card focus-visible:px-2 focus-visible:shadow-[inset_0_1px_0_rgba(0,0,0,0.04)]",
          "data-[state=open]:border-border-strong data-[state=open]:bg-card data-[state=open]:px-2 data-[state=open]:shadow-[inset_0_1px_0_rgba(0,0,0,0.04)]",
          "transition-[background-color,border-color,box-shadow,padding] duration-300 ease-out",
        ],
        /**
         * Only the right side grows — the left inset on hover/focus is
         * already the container's own `px-2`, and repeating it here would
         * indent the text twice.
         */
        input: [
          "px-0",
          "hover:pr-1",
          "focus:pr-1",
          "transition-[padding] duration-300 ease-out",
        ],
        /**
         * Focus keeps `border-lines`/`bg-background` rather than `Input`'s
         * `border-border-strong`/`bg-card` — a pre-existing inconsistency
         * between the two base components, left alone rather than fixed in
         * passing.
         *
         * Only `pl` grows, so the first character stays aligned with the
         * label above it.
         */
        textarea: [
          "rounded-md border border-transparent bg-transparent shadow-none",
          "min-h-20 p-0 py-2",
          "hover:bg-surface-hovered hover:pl-2",
          "focus-within:border-lines focus-within:bg-background focus-within:pl-2",
          "transition-[background-color,border-color,padding] duration-300 ease-out",
        ],
      },
      standard: {
        editableSelect: [
          "border-border-strong bg-card text-text h-9 rounded-lg shadow-[inset_0_1px_0_rgba(0,0,0,0.04)]",
          "placeholder:text-disabled",
          "focus:border-ring focus:ring-ring/30 focus:ring-2 focus:ring-offset-0",
          "transition-[box-shadow,border-color] duration-150",
        ],
        /**
         * `ConfigTextareaControl` draws its own `<textarea>` with older base
         * colours, so `standard` restates `Input`'s look: background,
         * border, radius, shadow, text and focus ring. It also sets a
         * minimum height, since an empty field would otherwise read as a
         * single cramped line. A floor, not a target — it auto-grows past it.
         */
        textarea: [
          "border-border-strong bg-card text-text min-h-20 rounded-lg px-3 py-2 shadow-[inset_0_1px_0_rgba(0,0,0,0.04)]",
          "placeholder:text-disabled",
          "focus-visible:border-ring focus-visible:ring-ring/30 focus-visible:ring-2 focus-visible:ring-offset-0",
          "transition-[box-shadow,border-color] duration-150",
        ],
      },
    },
  },
  defaultVariants: {
    appearance: "subtle",
  },
});

/**
 * How a field row places its label block against its control, keyed by the
 * field's `orientation`.
 *
 * `vertical`, the default, is the stacked shape: label, description, then
 * control.
 *
 * `horizontal` puts the label block in a **fixed-width** left column. Fixed
 * rather than content-sized is the whole point — a row knows nothing about
 * its siblings, so a shared width is the only thing that makes every row in
 * a section align down the page. `shrink-0` holds that width against a long
 * label, which wraps instead of stealing room from the control. The width
 * is capped at a third of the row, which still aligns: the rows of a
 * section share one width. A `vertical` row has nothing to align against,
 * so it pins no width: that would only truncate long labels for no gain.
 *
 * `items-start` rather than `items-center`: a two-line description would
 * otherwise drag the control down to the middle of the block, away from the
 * label it belongs to.
 */
export const configFieldRowVariants = tv({
  slots: {
    root: "py-3",
    label: "",
    /**
     * `min-w-0` on both orientations — without it a flex child refuses to
     * shrink below its content, and a long value pushes the row wider than
     * the card.
     */
    control: "min-w-0",
  },
  variants: {
    orientation: {
      vertical: {
        root: "flex flex-col gap-2",
      },
      horizontal: {
        root: "flex flex-row items-start gap-6",
        /**
         * 224px, or a third of the row when that is less (a drawer), so a
         * narrow row keeps two thirds for the control. Rows of one card
         * share a width, so their labels still line up.
         */
        label: "w-56 max-w-[33.333%] shrink-0",
        control: "flex flex-1 flex-col gap-1",
      },
    },
    described: {
      true: {},
      false: {},
    },
    /**
     * The control's appearance, which sets its height. Only an editable row
     * passes it, and only for a one-line control; without it nothing is
     * offset.
     */
    appearance: {
      subtle: {},
      standard: {},
    },
  },
  /**
   * A horizontal label with no description is one short line at the top of
   * a taller input, so it reads as floating above it. It moves down to the
   * input's middle: the label line is 16px, so half the difference to the
   * input's height (32px subtle, 36px standard). An offset, not centring the
   * row, so a textarea's label stays by its first line.
   */
  compoundVariants: [
    {
      orientation: "horizontal",
      described: false,
      appearance: "subtle",
      class: { label: "pt-2" },
    },
    {
      orientation: "horizontal",
      described: false,
      appearance: "standard",
      class: { label: "pt-2.5" },
    },
  ],
  defaultVariants: {
    orientation: "vertical",
  },
});

/**
 * How wide a control renders, keyed by the width its field declares under
 * `presentation`.
 *
 * No slots: this applies only to kinds whose control is a single primitive
 * input, so there is exactly one element to size. A composite control has
 * more than one part and needs its own sizing vocabulary rather than this
 * one widened to cover it.
 *
 * `w-20` is not an arbitrary pick — it is the width `ConfigPercentControl`
 * already gives its own number box, so a compact number field and a percent
 * field line up instead of being two guesses.
 */
export const configFieldWidthVariants = tv({
  variants: {
    width: {
      full: "",
      compact: "w-20",
    },
  },
  defaultVariants: {
    width: "full",
  },
});

/**
 * The `radio` variant of a `code` field — an option list that is always
 * open, so unlike `configFieldVariants` there is no at-rest/hover chrome
 * question to answer and `appearance` does not apply here.
 *
 * Each option is a full-width card rather than a bare radio and label: the
 * whole row is the click target, which is what makes a three-way choice
 * readable at a glance instead of asking the user to hit a 16px circle.
 */
export const configCodeRadioVariants = tv({
  slots: {
    group: "flex gap-2",
    option:
      "flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 transition-colors",
    /**
     * `mt-0.5` is the 2px that centres a 16px control against the 20px line
     * box of `Text`'s `bodyMd`. With `items-start` above it, that lands a
     * one-line option exactly centred and keeps the radio on the *first*
     * line of one that carries a description, rather than floating to the
     * middle of the block.
     */
    input: "mt-0.5 size-4 shrink-0 accent-current",
    /**
     * Wraps label and description so they stack as one block beside the
     * radio, instead of the description becoming a third flex sibling of it.
     */
    content: "flex flex-col gap-0.5",
  },
  variants: {
    orientation: {
      vertical: { group: "flex-col" },
      /**
       * `flex-1 basis-0` gives every option the same width regardless of
       * label length, so the row reads as one set of equals. `min-w-40` is
       * the floor at which they wrap to a second line instead of squeezing
       * — a horizontal option is only worth it while its label still fits.
       */
      horizontal: {
        group: "flex-row flex-wrap",
        option: "min-w-40 flex-1 basis-0",
      },
    },
    /**
     * `critical` replaces `default` rather than layering on top of it, so an
     * unset required field tones every option at once — the radio group's
     * equivalent of the destructive placeholder a `select` shows, which it
     * has no room for once the options are already on screen. A selected
     * option is never critical: choosing one is exactly what clears it.
     */
    state: {
      default: { option: "border-border hover:bg-surface-hovered" },
      selected: { option: "border-primary bg-surface-selected" },
      critical: { option: "border-destructive/50 hover:bg-surface-hovered" },
    },
  },
  defaultVariants: {
    orientation: "vertical",
    state: "default",
  },
});

/**
 * Native `::placeholder` recoloring for `Input`/`Textarea` (via
 * `inputClassName`/`className`) — a required field with no value keeps the
 * critical toning read mode gave it (the `Missing` badge), rather than
 * flattening to the same neutral placeholder an optional-and-unset field
 * gets. Not a `::placeholder` pseudo-element for `Select` — see
 * `criticalSelectPlaceholderClassName`.
 */
export const criticalPlaceholderClassName = "placeholder:text-destructive";

/**
 * `Select`'s empty-state text is rendered content (`SelectValue`'s
 * `placeholder` prop), not a native `::placeholder` pseudo-element, so it
 * takes a plain text-color utility instead of the `placeholder:` variant.
 */
export const criticalSelectPlaceholderClassName = "text-destructive";

/**
 * `FormMessage`'s own `text-sm font-medium` matches the field label it sits
 * under, so a validation note competes with the value it is about — an
 * inline field's message reads as an aside instead.
 */
export const inlineFieldMessageClassName = "text-xs font-normal";

/**
 * Needs for i18next-scanner
 */
// t('minutes')
// t('hours')
// t('days')
/**
 * Values are i18n keys — always pass through `t()` before rendering.
 */
export const durationUnitLabelKeys: Record<DurationUnit, string> = {
  minute: "minutes",
  hour: "hours",
  day: "days",
};

/**
 * Needs for i18next-scanner
 */
// t('Dr. (male)')
// t('Dr. (female)')
/**
 * Kora says the title to patients as stored, so the values stay the Spanish
 * text AKB writes. Only the labels follow the user's language. Prod has
 * other titles too (`Licda`, `Lic`, `MSc`), which is why this suggests
 * rather than restricts.
 */
export const practitionerTitleSuggestions: ConfigTextSuggestion[] = [
  { value: "Dr", label: "Dr. (male)" },
  { value: "Dra", label: "Dr. (female)" },
];

/**
 * Needs for i18next-scanner
 */
// t('Knowledge')
// t('Scheduling')
// t('Member')
/**
 * The Practitioners card's badge per source. "Knowledge" for AKB, after the
 * Agent Knowledge Base; "Scheduling" after Agent Scheduling, which finds these
 * practitioners.
 */
export const practitionerSourceLabels: Record<PractitionerSource, string> = {
  akb: "Knowledge",
  scheduling: "Scheduling",
  member: "Member",
};

/**
 * Needs for i18next-scanner
 */
// t('Español')
// t('English')
/**
 * A record's FHIR `language`, by the language's own name. Both locale files
 * map each name to itself, so a Spanish user still reads "English", not
 * "Inglés". A code missing here shows as stored.
 */
export const fhirLanguageLabels: Record<string, string> = {
  es: "Español",
  en: "English",
};

/**
 * The zones the timezone picker lists before a search: one per country of
 * Latin America, Mexico, Central America and the Caribbean, where the
 * clinics are, under the capital's zone. The United States and Canada are
 * left out. A search still finds every zone, so a clinic elsewhere types
 * its country or city.
 */
export const timezoneShortList: string[] = [
  "America/Antigua",
  "America/Argentina/Buenos_Aires",
  "America/Asuncion",
  "America/Barbados",
  "America/Belize",
  "America/Bogota",
  "America/Caracas",
  "America/Costa_Rica",
  "America/Dominica",
  "America/El_Salvador",
  "America/Grenada",
  "America/Guatemala",
  "America/Guayaquil",
  "America/Guyana",
  "America/Havana",
  "America/Jamaica",
  "America/La_Paz",
  "America/Lima",
  "America/Managua",
  "America/Mexico_City",
  "America/Montevideo",
  "America/Nassau",
  "America/Panama",
  "America/Paramaribo",
  "America/Port-au-Prince",
  "America/Port_of_Spain",
  "America/Puerto_Rico",
  "America/Santiago",
  "America/Santo_Domingo",
  "America/Sao_Paulo",
  "America/St_Kitts",
  "America/St_Lucia",
  "America/St_Vincent",
  "America/Tegucigalpa",
];
